import { useState, useEffect, useRef, useCallback } from 'react';

interface AudioAlert {
  id: string;
  type: 'time_alert' | 'urgent_alert' | 'system_alert';
  message: string;
  severity: 'low' | 'medium' | 'high';
  timestamp: Date;
  autoClose?: number; // milisegundos para auto-cerrar
}

interface UseAudioAlertsOptions {
  enableSound?: boolean;
  soundVolume?: number; // 0-1
  enableNotifications?: boolean;
}

export const useAudioAlerts = (options: UseAudioAlertsOptions = {}) => {
  const {
    enableSound = true,
    soundVolume = 0.7,
    enableNotifications = true
  } = options;

  const [alerts, setAlerts] = useState<AudioAlert[]>([]);
  const [isMuted, setIsMuted] = useState(() => {
    // Recuperar estado del localStorage
    const saved = localStorage.getItem('alertsMuted');
    return saved ? JSON.parse(saved) : false;
  });
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('alertsSoundEnabled');
    return saved ? JSON.parse(saved) : enableSound;
  });

  const audioContextRef = useRef<AudioContext | null>(null);
  const alertSoundsRef = useRef<{ [key: string]: AudioBuffer }>({});

  // Inicializar AudioContext y cargar sonidos
  useEffect(() => {
    if (typeof window !== 'undefined' && soundEnabled) {
      initializeAudio();
    }

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [soundEnabled]);

  // Guardar configuración en localStorage
  useEffect(() => {
    localStorage.setItem('alertsMuted', JSON.stringify(isMuted));
  }, [isMuted]);

  useEffect(() => {
    localStorage.setItem('alertsSoundEnabled', JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  const initializeAudio = async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      // Crear sonidos sintéticos para diferentes tipos de alertas
      await createSyntheticSounds();
    } catch (error) {
    }
  };

  const createSyntheticSounds = async () => {
    if (!audioContextRef.current) return;

    const ctx = audioContextRef.current;

    // Sonido para alertas de tiempo (beep suave)
    alertSoundsRef.current.time_alert = createBeepSound(ctx, 800, 0.3, 0.2);

    // Sonido para alertas urgentes (beep más fuerte)
    alertSoundsRef.current.urgent_alert = createBeepSound(ctx, 1000, 0.5, 0.4);

    // Sonido para alertas del sistema (tono más grave)
    alertSoundsRef.current.system_alert = createBeepSound(ctx, 600, 0.4, 0.3);
  };

  const createBeepSound = (ctx: AudioContext, frequency: number, duration: number, volume: number): AudioBuffer => {
    const sampleRate = ctx.sampleRate;
    const numSamples = Math.floor(sampleRate * duration);
    const buffer = ctx.createBuffer(1, numSamples, sampleRate);
    const channelData = buffer.getChannelData(0);

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      // Generar onda sinusoidal con fade in/out
      let fadeMultiplier = 1;
      if (i < numSamples * 0.1) {
        fadeMultiplier = i / (numSamples * 0.1);
      } else if (i > numSamples * 0.9) {
        fadeMultiplier = (numSamples - i) / (numSamples * 0.1);
      }

      channelData[i] = Math.sin(2 * Math.PI * frequency * t) * volume * fadeMultiplier;
    }

    return buffer;
  };

  const playSound = useCallback(async (alertType: string) => {
    if (!soundEnabled || isMuted || !audioContextRef.current) return;

    try {
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      const buffer = alertSoundsRef.current[alertType];
      if (!buffer) return;

      const source = audioContextRef.current.createBufferSource();
      const gainNode = audioContextRef.current.createGain();

      source.buffer = buffer;
      gainNode.gain.value = soundVolume;

      source.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      source.start();
    } catch (error) {
    }
  }, [soundEnabled, isMuted, soundVolume]);

  const showBrowserNotification = useCallback((alert: AudioAlert) => {
    if (!enableNotifications || !('Notification' in window)) return;

    if (Notification.permission === 'granted') {
      const notification = new Notification(`LVPV - ${alert.type.replace('_', ' ').toUpperCase()}`, {
        body: alert.message,
        icon: '/lvpv-logo.svg',
        badge: '/lvpv-logo.svg',
        tag: alert.id, // Evita duplicados
        requireInteraction: false, // Permitir auto-cierre para todas las notificaciones
        silent: isMuted
      });

      // Auto-cerrar después de 8 segundos (tiempo suficiente para leer)
      setTimeout(() => {
        notification.close();
      }, 8000);
    }
  }, [enableNotifications, isMuted]);

  const addAlert = useCallback((alertData: Omit<AudioAlert, 'id' | 'timestamp'>) => {
    const alert: AudioAlert = {
      ...alertData,
      id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };

    setAlerts(prev => [alert, ...prev].slice(0, 50)); // Mantener solo 50 alertas

    // Reproducir sonido
    playSound(alert.type);

    // Mostrar notificación del navegador
    showBrowserNotification(alert);

    // Auto-cerrar si está configurado
    if (alert.autoClose) {
      setTimeout(() => {
        removeAlert(alert.id);
      }, alert.autoClose);
    }

    return alert.id;
  }, [playSound, showBrowserNotification]);

  const removeAlert = useCallback((alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  }, []);

  const clearAllAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => !prev);
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  // Métodos de conveniencia para tipos específicos de alertas
  const addTimeAlert = useCallback((message: string, severity: 'low' | 'medium' | 'high' = 'medium') => {
    // Todas las alertas de tiempo se auto-cierran
    const autoCloseTime = severity === 'low' ? 5000 :
                          severity === 'medium' ? 8000 : 10000;
    return addAlert({
      type: 'time_alert',
      message,
      severity,
      autoClose: autoCloseTime
    });
  }, [addAlert]);

  const addUrgentAlert = useCallback((message: string) => {
    return addAlert({
      type: 'urgent_alert',
      message,
      severity: 'high',
      autoClose: 10000 // Auto-cerrar después de 10 segundos
    });
  }, [addAlert]);

  const addSystemAlert = useCallback((message: string, severity: 'low' | 'medium' | 'high' = 'medium') => {
    // Todas las alertas de sistema se auto-cierran
    const autoCloseTime = severity === 'low' ? 3000 :
                          severity === 'medium' ? 5000 : 8000;
    return addAlert({
      type: 'system_alert',
      message,
      severity,
      autoClose: autoCloseTime
    });
  }, [addAlert]);

  return {
    alerts,
    isMuted,
    soundEnabled,
    addAlert,
    addTimeAlert,
    addUrgentAlert,
    addSystemAlert,
    removeAlert,
    clearAllAlerts,
    toggleMute,
    toggleSound,
    requestNotificationPermission
  };
};

export default useAudioAlerts;